/* =========================================================
   PJ WISHLIST — core engine
   Stores a list of product handles in localStorage. Guests use
   a shared "guest" key; logged-in customers get their own key
   (window.__pjWishlistCustomerId, set inline in header.liquid),
   so their wishlist stays consistent across sessions on this
   browser. A guest wishlist is merged in automatically the
   first time they're detected as logged in.

   NOTE ON ACCOUNT SYNC: true cross-device sync (the same
   wishlist showing up on a different phone/computer) would
   require writing to a Shopify customer metafield from an
   authenticated source (a Shopify app or Flow), which isn't
   something theme-only JavaScript can safely do — that always
   requires a private access token, which must never be exposed
   in a browser. What this DOES give you: a reliable wishlist
   that persists across visits, page refreshes, and browser
   restarts on the same device, whether the shopper is a guest
   or logged in.
   ========================================================= */
(function () {
  'use strict';

  function storageKey() {
    return window.__pjWishlistCustomerId
      ? 'pj_wishlist_customer_' + window.__pjWishlistCustomerId
      : 'pj_wishlist_guest';
  }

  function read() {
    try {
      var raw = localStorage.getItem(storageKey());
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(storageKey(), JSON.stringify(list));
    } catch (e) {
      /* localStorage unavailable (private mode, quota, etc.) — fail silently */
    }
  }

  function has(handle) {
    return read().indexOf(handle) !== -1;
  }

  function add(handle) {
    var list = read();
    if (list.indexOf(handle) === -1) {
      list.push(handle);
      write(list);
      notify('add', handle);
    }
  }

  function remove(handle) {
    var list = read();
    var idx = list.indexOf(handle);
    if (idx !== -1) {
      list.splice(idx, 1);
      write(list);
      notify('remove', handle);
    }
  }

  function toggle(handle) {
    if (has(handle)) {
      remove(handle);
      return false;
    }
    add(handle);
    return true;
  }

  function count() {
    return read().length;
  }

  function notify(action, handle) {
    document.dispatchEvent(new CustomEvent('pj:wishlist:change', {
      detail: { action: action, handle: handle, count: count() }
    }));
    updateBadge();
  }

  function updateBadge() {
    var n = count();
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = n;
      el.hidden = n === 0;
    });
  }

  function migrateGuestIfNeeded() {
    if (!window.__pjWishlistCustomerId) return;
    var flag = 'pj_wishlist_migrated_' + window.__pjWishlistCustomerId;
    try {
      if (localStorage.getItem(flag)) return;
      var guestRaw = localStorage.getItem('pj_wishlist_guest');
      if (guestRaw) {
        var guestList = JSON.parse(guestRaw) || [];
        var custList = read();
        guestList.forEach(function (h) {
          if (custList.indexOf(h) === -1) custList.push(h);
        });
        write(custList);
      }
      localStorage.setItem(flag, '1');
    } catch (e) {}
  }

  function bindButtons(root) {
    (root || document).querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      if (btn.__pjWishlistBound) return;
      btn.__pjWishlistBound = true;

      var handle = btn.getAttribute('data-wishlist-toggle');
      btn.classList.toggle('is-active', has(handle));
      btn.setAttribute('aria-pressed', has(handle) ? 'true' : 'false');

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var nowActive = toggle(handle);
        btn.classList.toggle('is-active', nowActive);
        btn.setAttribute('aria-pressed', nowActive ? 'true' : 'false');
        btn.classList.add('pj-wishlist-pulse');
        window.setTimeout(function () {
          btn.classList.remove('pj-wishlist-pulse');
        }, 380);
      });
    });
  }

  window.PJWishlist = {
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    getAll: read,
    count: count,
    bindButtons: bindButtons
  };

  document.addEventListener('DOMContentLoaded', function () {
    migrateGuestIfNeeded();
    bindButtons(document);
    updateBadge();
  });
})();
